import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface ConfirmOptions {
  title?: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'default';
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return context;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        options,
        resolve,
      });
    });
  }, []);

  const handleConfirm = () => {
    if (confirmState) {
      confirmState.resolve(true);
      setConfirmState(null);
    }
  };

  const handleCancel = () => {
    if (confirmState) {
      confirmState.resolve(false);
      setConfirmState(null);
    }
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      
      {/* Plain fixed overlay. DaisyUI 5's `.modal-open` never applies its open
          styles under Tailwind 3 (the box stays visibility:hidden), so the
          dialog is laid out here with explicit classes instead. */}
      {confirmState?.isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
          onClick={handleCancel}
        >
          <div
            className="card relative w-full max-w-md rounded-2xl border border-neutral-800 bg-neutral-900 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Icon */}
            <div className="flex flex-col items-center text-center gap-4 pt-2 pb-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                confirmState.options.type === 'danger'
                  ? 'bg-red-500/15 text-red-500'
                  : 'bg-primary-500/15 text-primary-500'
              }`}>
                <AlertTriangle className="w-7 h-7" />
              </div>
              <h3 className="font-bold text-xl">
                {confirmState.options.title || 'Confirm action'}
              </h3>
            </div>

            {/* Content */}
            <div className="text-center text-neutral-400 whitespace-pre-line leading-relaxed px-2">
              {confirmState.options.message}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={handleConfirm}
                className={`btn w-full ${
                  confirmState.options.type === 'danger'
                    ? 'btn-danger'
                    : 'btn-primary'
                }`}
              >
                {confirmState.options.confirmText || 'Confirm'}
              </button>
              <button
                onClick={handleCancel}
                className="btn btn-ghost w-full"
              >
                {confirmState.options.cancelText || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}


